import { useQueryClient } from '@tanstack/react-query';
import { cloneElement } from 'react';

import NotScheduledView from './NotScheduledView';
import ScheduledView from './ScheduledView';
import { ScheduleCallProps } from './ScheduleCall.interface';

import { Calendar, CalendarStatus } from 'services/calendly/queries/useCalendlyLinks/useCalendlyLinks.interface';
import CalendlyDialog from 'modules/common/components/CalendlyDialog';
import useDialogControl from 'modules/IMC/MessageCenter/hooks/useDialogControl';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';

export default function ScheduleCall({
    member,
    selectedCalendar,
    specialistName,
    ctaComponent = null,
    onScheduleClick,
}: ScheduleCallProps) {
    const queryClient = useQueryClient();

    const { open: calendlyOpen, handleOpenDialog, handleCloseDialog } = useDialogControl();

    // Use custom click handler if provided, otherwise use default Calendly dialog
    const handleScheduleClick = onScheduleClick || handleOpenDialog;

    const calendarStatus = selectedCalendar?.status ?? CalendarStatus.NotScheduled;
    const scheduledTime = selectedCalendar?.start_time ?? null;

    const handleScheduled = () => {
        handleCloseDialog();

        queryClient.setQueryData(['CalendlyLinks'], (oldData: any) => {
            if (!oldData || !Array.isArray(oldData)) {
                return oldData;
            }

            const updatedData = oldData.map((item) => {
                if (item.specialist_name !== specialistName) {
                    return item;
                }

                const updatedCalendars = Object.fromEntries(
                    Object.entries(item.calendars as Record<string, Calendar>).map(([key, calendar]) => {
                        if (calendar.schedule_url === selectedCalendar?.schedule_url) {
                            return [
                                key,
                                {
                                    ...calendar,
                                    status: CalendarStatus.Scheduled,
                                    start_time: new Date().toISOString(),
                                },
                            ];
                        }
                        return [key, calendar];
                    })
                );

                return {
                    ...item,
                    calendars: updatedCalendars,
                };
            });

            return updatedData;
        });

        collectiveTracking.track({
            name: 'Help Center Call Scheduled',
            properties: {
                category: 'help_center',
            },
        });

        queryClient.invalidateQueries({
            queryKey: ['CalendlyLinks'],
        });
    };

    const statusComponentMap = {
        [CalendarStatus.NotScheduled]: <NotScheduledView onSchedule={handleScheduleClick} />,
        [CalendarStatus.Scheduled]: (
            <ScheduledView
                scheduledTime={scheduledTime}
                specialistName={specialistName}
                onReschedule={handleOpenDialog}
            />
        ),
        [CalendarStatus.InProgress]: (
            <ScheduledView
                scheduledTime={scheduledTime}
                specialistName={specialistName}
                onReschedule={handleOpenDialog}
            />
        ),
        [CalendarStatus.Canceled]: <NotScheduledView onSchedule={handleScheduleClick} />,
        [CalendarStatus.Completed]: <NotScheduledView onSchedule={handleScheduleClick} />,
    };

    const calendarUrl =
        {
            [CalendarStatus.Scheduled]: selectedCalendar?.reschedule_url || selectedCalendar?.schedule_url,
            [CalendarStatus.InProgress]: selectedCalendar?.reschedule_url || selectedCalendar?.schedule_url,
            [CalendarStatus.Canceled]: selectedCalendar?.schedule_url,
            [CalendarStatus.NotScheduled]: selectedCalendar?.schedule_url,
            [CalendarStatus.Completed]: selectedCalendar?.schedule_url,
        }[calendarStatus] ?? '';

    const wrappedCtaComponent = ctaComponent
        ? cloneElement(ctaComponent as React.ReactElement, {
              onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  handleOpenDialog();
                  (ctaComponent as React.ReactElement).props.onClick?.(e);
              },
          })
        : null;

    return (
        <>
            {wrappedCtaComponent ?? statusComponentMap[calendarStatus] ?? (
                <NotScheduledView onSchedule={handleScheduleClick} />
            )}
            {/* Only render CalendlyDialog when using default behavior (not custom onScheduleClick) */}
            {!onScheduleClick && (
                <CalendlyDialog
                    open={calendlyOpen}
                    memberUuid={member?.uuid}
                    prefill={{
                        email: member?.email ?? '',
                        name: member?.nickname ?? member?.first_name ?? member?.fullname ?? '',
                        company: member?.businessname ?? '',
                    }}
                    onClose={handleCloseDialog}
                    onEventScheduled={handleScheduled}
                    url={calendarUrl}
                />
            )}
        </>
    );
}
